import { useCanWrite } from '@artic/api-client';
import { Badge } from '@artic/ui/badge';
import { WorkloadCategoryDots } from '@artic/ui/badges-and-status-labels/workload-category-dots';
import { Button } from '@artic/ui/button';
import { StandardDate } from '@artic/ui/date-tooltip';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { Skeleton } from '@artic/ui/skeleton';
import { StatusFilterBar, type StatusFilterItem } from '@artic/ui/status-filter-bar';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BillOfMaterialsChip } from '../../features/bill-of-materials/bill-of-materials-chip';
import { usePartnerId } from '../../features/inbox/use-partner-name';
import { ProductLabelFilters } from '../../features/products/product-label-filters';
import {
  partnerContainersQueryOptions,
  productListQueryOptions,
} from '../../features/products/queries';
import {
  billOfMaterialsFilterCounts,
  buildProductLabelFilters,
  filterByBillOfMaterials,
  filterByLabels,
  filterProducts,
  productSearchParsers,
  resolveLabelPicks,
  type BillOfMaterialsFilter,
} from '../../features/products/search';
import {
  productTotalLabourHoursLabel,
  productWorkloadCategory,
} from '../../features/products/workload-display';

export const Route = createFileRoute('/products/')({
  component: ProductListContent,
});

function ProductListContent() {
  const { t } = useTranslation('materials');
  const { t: tCommon } = useTranslation('common');
  const partnerId = usePartnerId();
  // Permissions v1 (catalog section): a read-only user never sees the entry
  // point to create a new Product.
  const canWriteCatalog = useCanWrite('catalog');
  const workloadCategoriesEnabled = useFeatureFlag('workload_categories');
  const billOfMaterialsEnabled = useFeatureFlag('bill_of_materials');
  const { data: products, isLoading, isError } = useQuery(productListQueryOptions(partnerId));
  const { data: partnerContainers } = useQuery({
    ...partnerContainersQueryOptions(partnerId),
    enabled: !!partnerId,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const [q, setQ] = useQueryState('q', productSearchParsers.q);
  const [bom, setBom] = useQueryState('bom', productSearchParsers.bom);
  const [labels] = useQueryState('labels', productSearchParsers.labels);
  const labelFilters = useMemo(
    () => buildProductLabelFilters(partnerContainers ?? [], products ?? []),
    [partnerContainers, products],
  );

  // ENG-1022: the Bill of Materials filter counts follow the search, and the
  // label filters too.
  const searched = products ? filterProducts(products, q) : [];
  const labelPicks = resolveLabelPicks(labelFilters, labels);
  const labelled = filterByLabels(searched, labelPicks);
  const bomCounts = billOfMaterialsFilterCounts(labelled);
  const filtered = billOfMaterialsEnabled ? filterByBillOfMaterials(labelled, bom) : labelled;
  const bomFilterItems: StatusFilterItem<BillOfMaterialsFilter | 'all'>[] = [
    { key: 'all', label: t('productsFilter.all'), count: bomCounts.all, selected: bom === null },
    { key: 'has', label: t('productsFilter.has'), count: bomCounts.has, selected: bom === 'has' },
    {
      key: 'none',
      label: t('productsFilter.none'),
      count: bomCounts.none,
      selected: bom === 'none',
    },
  ];

  // Containers the partner has flagged as label-containers, ordered by their
  // configured display order so the pills come out in a predictable sequence.
  const labelContainerIds = useMemo(() => {
    const ordered = (partnerContainers ?? [])
      .filter((c) => c.use_for_label)
      .slice()
      .sort((a, b) => a.display_order - b.display_order)
      .map((c) => c.container_id);
    return { ids: new Set(ordered), order: ordered };
  }, [partnerContainers]);

  const showLabelsColumn = labelContainerIds.ids.size > 0;

  if (isError) {
    return (
      <div className="text-destructive py-10 text-center text-sm">
        Failed to load products. Please try again.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Products</h1>
        {canWriteCatalog && (
          <Button asChild size="sm">
            <Link to="/products/new">New Product</Link>
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search products..."
          value={q ?? ''}
          onChange={(e) => void setQ(e.target.value || null)}
          className="max-w-sm"
        />
        <ProductLabelFilters filters={labelFilters} />
        {/* The counts are only true once the products are in: no "0 All" while loading. */}
        {billOfMaterialsEnabled &&
          (products ? (
            <div role="group" aria-label={t('productsFilter.label')}>
              <StatusFilterBar
                items={bomFilterItems}
                onSelect={(key) => void setBom(key === 'all' ? null : key)}
              />
            </div>
          ) : (
            <Skeleton className="h-8 w-72 rounded" />
          ))}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-muted-foreground py-10 text-center text-sm">
          {q || labelPicks.size > 0 || (billOfMaterialsEnabled && bom)
            ? 'No products match your search.'
            : 'No products yet.'}
        </div>
      ) : (
        <div className="@container overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b text-left">
                <th className="px-4 py-2 font-medium">Product Code</th>
                <th className="px-4 py-2 font-medium">Name</th>
                {showLabelsColumn && <th className="px-4 py-2 font-medium">Labels</th>}
                {workloadCategoriesEnabled && (
                  <>
                    <th className="px-4 py-2 text-right font-medium">Total Labour Hours</th>
                    <th className="px-4 py-2 font-medium">Workload Category</th>
                  </>
                )}
                <th className="px-4 py-2 font-medium">Run Sheet</th>
                {billOfMaterialsEnabled && (
                  <th className="px-4 py-2 font-medium">{tCommon('terms.billOfMaterials')}</th>
                )}
                <th className="px-4 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => {
                // Build pills in the partner's configured container order so
                // products line up visually across rows.
                const selectionsByContainer = new Map(
                  product.container_selections.map((s) => [s.container_id, s]),
                );
                const labelPills = labelContainerIds.order
                  .map((containerId) => selectionsByContainer.get(containerId))
                  .filter((s): s is (typeof product.container_selections)[number] => s != null);

                return (
                  <tr
                    key={product.product_id}
                    data-product-key={product.product_key}
                    className="hover:bg-muted/30 border-b transition-colors"
                  >
                    {/* A long Code or Name is cut to one line with "…" so the table
                        never outgrows the page: break-all lets the column shrink,
                        line-clamp keeps one line, and the title carries the full
                        text. Auto layout shrinks every column in proportion, so
                        min-w keeps a typical (~20 character) code and name whole; a
                        table under 70rem (1280px with the sidebar open) only has
                        room for the smaller floor. */}
                    <td className="px-4 py-2">
                      <Link
                        to="/products/$productId"
                        params={{ productId: product.product_id }}
                        title={product.product_key}
                        className="text-primary line-clamp-1 w-fit min-w-28 font-mono text-xs font-medium break-all hover:underline @min-[70rem]:min-w-40"
                      >
                        {product.product_key}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className="line-clamp-1 min-w-24 break-all @min-[70rem]:min-w-48"
                        title={product.product_name}
                      >
                        {product.product_name}
                      </span>
                    </td>
                    {showLabelsColumn && (
                      <td className="px-4 py-2">
                        {labelPills.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {/* A pill is capped at max-w-40 so one long label cannot
                                widen the table; the text sits in its own min-w-0
                                span because truncate is inert on a flex child. */}
                            {labelPills.map((s) => (
                              <Label
                                key={s.container_id}
                                className="bg-secondary text-secondary-foreground max-w-40 rounded-4xl px-2 py-0.5 text-xs whitespace-nowrap"
                                title={`${s.container_name}: ${s.option_name}`}
                              >
                                <span className="min-w-0 truncate">{s.option_name}</span>
                              </Label>
                            ))}
                          </div>
                        )}
                      </td>
                    )}
                    {workloadCategoriesEnabled &&
                      (() => {
                        const category = productWorkloadCategory(product);
                        const totalLabourHours = productTotalLabourHoursLabel(product);
                        return (
                          <>
                            <td
                              className={
                                totalLabourHours === '—'
                                  ? 'text-muted-foreground px-4 py-2 text-right'
                                  : 'px-4 py-2 text-right'
                              }
                            >
                              {totalLabourHours}
                            </td>
                            <td className="px-4 py-2">
                              {category.kind === 'category' ? (
                                <WorkloadCategoryDots category={category.category} />
                              ) : category.kind === 'baseline' ? (
                                <WorkloadCategoryDots category={null} baseline />
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </>
                        );
                      })()}
                    <td className="px-4 py-2">
                      {product.bill_of_operations_id ? (
                        <Badge variant="outline">Linked</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {billOfMaterialsEnabled && (
                      <td data-column="bill_of_materials" className="px-4 py-2">
                        <BillOfMaterialsChip product={product} />
                      </td>
                    )}
                    <td className="text-muted-foreground px-4 py-2 text-xs">
                      <StandardDate value={product.product_created_at} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
